import {
  ALL_COURT_FORMS_ACCESSES,
  FeatureAccessType,
} from '../featureAccesses';
import { useCurrentOrg, getOrg } from './useCurrentOrg';
import { getRegion, Region } from '~/src/regions/config/utils';
import { stateCodeToStateName } from '~/src/utils/stateCodeToStateName';
import { queryClient } from '~/src/utils/queryClient';
import { Organization } from './useMemberships';

export type StateCode = keyof typeof stateCodeToStateName;

export const canOrgUseFeatureAccess = (
  access: FeatureAccessType,
  org: Organization | undefined,
) => org?.feature_accesses[access]?.can_use_feature;

export const useFeatureAccess = (access: FeatureAccessType) =>
  canOrgUseFeatureAccess(access, useCurrentOrg().org);

export const useCheckFeatureAccess = () => {
  const org = useCurrentOrg().org!;
  return (access: FeatureAccessType) => canOrgUseFeatureAccess(access, org);
};

export const useCbsIntegrationEnabled = () =>
  useFeatureAccess('CBS_INTEGRATION');

export const useMavenIntegrationEnabled = () =>
  useFeatureAccess('MAVEN_INTEGRATION');

export const useCustomXmlTemplateFormatEnabled = () =>
  useFeatureAccess('CUSTOM_XML_TEMPLATE_FORMAT');

// Copying Clio Draft templates to Clio Work: the Library banner and the copy page.
export const useDraftToWorkTemplateMigrationEnabled = () =>
  useFeatureAccess('DRAFT_TO_WORK_TEMPLATE_MIGRATION');

// Orgs on the Clio for Word add-in (CUSTOM_XML_TEMPLATE_FORMAT) are pushed
// toward that experience instead of the in-app AI Drafting Specialist, so the
// specialist is hidden even when DRAFTING_AGENT is granted.
export const useDraftingAgentEnabled = () => {
  const draftingAgent = useFeatureAccess('DRAFTING_AGENT');
  const customXmlFormat = useFeatureAccess('CUSTOM_XML_TEMPLATE_FORMAT');
  return draftingAgent && !customXmlFormat;
};

// Clio Questionnaires is enabled on all UP accounts
export const useClioQuestionnairesEnabled = () => useFeatureAccess('TAGS_V2');

export const useWordDocumentAutomationEnabled = () =>
  useFeatureAccess('WORD_DOCUMENT_AUTOMATION');

export const useCourtFormsEnabled = () => {
  const region = getRegion();
  const hasAccess = ALL_COURT_FORMS_ACCESSES.some(useCheckFeatureAccess());
  if (region === Region.US) {
    return hasAccess;
  }
  return true;
};

export const useFederalCourtFormsEnabled = () =>
  useFeatureAccess('FEDERAL_FORMS');

export const useIsUnifiedPlatform = () => useFeatureAccess('TAGS_V2');

export const useSubscriptionV2Enabled = () =>
  useFeatureAccess('SUBSCRIPTIONS_V2');

export const useIsClioLead = () => useFeatureAccess('MANAGE_LEAD');

export const useIsClioFree = () => useFeatureAccess('CLIO_FREE');

export const useSingleMatterImportEnabled = () =>
  useFeatureAccess('SINGLE_MATTER_IMPORT');

export const useTemplateDefaultValuesEnabled = () =>
  useFeatureAccess('TEMPLATE_DEFAULT_VALUES');

export const useCheckStateFormsEnabled = () => {
  const check = useCheckFeatureAccess();
  return (stateCode: StateCode) =>
    check(`${stateCode}_JC_FORMS` as FeatureAccessType);
};

export const isFeatureAccessEnabled = (
  access: FeatureAccessType,
  client = queryClient,
) => canOrgUseFeatureAccess(access, getOrg(client));

export const useIsCanary = (): boolean =>
  useFeatureAccess('DRAFT_SIGNAL_SUPPORT') === true;

export const useIsMaintenanceBannerEnabled = (): boolean =>
  useFeatureAccess('MAINTENANCE_BANNER') === true;

export const useIsIdentityLoginDisabled = (): boolean =>
  useFeatureAccess('DISABLE_IDENTITY_LOGIN') === true;

export const useAIFieldMapperEnabled = (): boolean =>
  useFeatureAccess('AI_FIELD_MAPPER') === true;

export const useDdhmEditEnabled = (): boolean =>
  useFeatureAccess('DDHM_EDIT') === true;

export const useEnableQualtrics = (): boolean =>
  useFeatureAccess('QUALTRICS_SURVEY') === true;

export const usePracticeAreaStandardContentEnabled = (): boolean =>
  useFeatureAccess('PRACTICE_AREA_STANDARD_CONTENT') === true;

export const useFederalFormsCategoriesEnabled = (): boolean =>
  useFeatureAccess('FEDERAL_FORMS_CATEGORIES') === true;

export const useCourtFormMasterSearchEnabled = (): boolean =>
  useFeatureAccess('COURT_FORM_MASTER_SEARCH') === true;

export const useAnnotationsDateFieldPickerEnabled = (): boolean =>
  useFeatureAccess('ANNOTATIONS_DATE_FIELD_PICKER') === true;
